// Kuika data-type coverage matrix — keep this in sync with
// `EKuikaDataTypes` (Backend/Kuika.Common/Enums/EKuikaDataTypes.cs)
// and the type predicates in cell-editor.tsx. When a new type is
// added to the enum, decide which lane it belongs in here:
//
//   Inline-validated (this file):
//     String     — required + maxLength
//     Guid       — required + 5-format regex (D / N / B / P / X)
//     Int16/32/64, AutoIncrement — required + range + integer-only
//     Decimal    — required + scale + plain-number format
//     Double     — required + plain-number format
//     Date       — required + ISO regex + calendar correctness
//     Datetime   — required + ISO regex + calendar correctness
//     DatetimeOffset — required + ISO + offset suffix
//     Location   — required + {latitude, longitude} both finite, in
//                  [-90, 90] / [-180, 180] respectively
//
//   Editor-restricted (input controls shape; freeform validation N/A):
//     Boolean    — checkbox; invalid values impossible.
//     Image, Signature, FileContent, VarBinary, Binary, Bytea,
//     ByteArray — file picker that stores base64. The picker only
//                 accepts whatever the OS file dialog returns, so the
//                 wire payload is correct by construction.
//
//   Server-generated (skipped by isServerGeneratedColumn):
//     AutoIncrement / identity / default-having columns — never
//     required at insert time, no client validation.
//
//   Non-editable in this grid (no input rendered):
//     Object — Kuika's catch-all fallback type; no canonical shape, so
//              freeform editing here would invite silent corruption.
//              Edit via the proper widget on a form.
//
// If you add a new kuikaDataType, pick a lane and update both files.
import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import {
  IColumnValueMap,
  IPendingChanges,
  IPendingRowInsert,
  IPendingRowUpdate,
  isPersistableInsert,
  RowPkString
} from "./pending-changes-reducer";

// Mirrors the formats accepted by .NET `Guid.TryParse` so local validation
// never rejects a value the backend would happily persist:
//   N — 32 hex digits, no separators
//   D — 8-4-4-4-12 hyphenated
//   B — braced: `{D}`
//   P — parenthesised: `(D)`
//   X — `{0xHHHHHHHH,0xHHHH,0xHHHH,{0xHH,0xHH,0xHH,0xHH,0xHH,0xHH,0xHH,0xHH}}`
//       .NET tolerates variable hex widths inside each chunk (e.g. `0x1`
//       instead of `0x01`) and whitespace around the commas, so we do too.
// `guid-typescript`'s `Guid.isGuid` only accepts D form, which is stricter
// than the backend and would block legitimate input.
const GUID_HEX_GROUP: string = "[0-9a-fA-F]";
const GUID_D_FORM: string = `${GUID_HEX_GROUP}{8}-${GUID_HEX_GROUP}{4}-${GUID_HEX_GROUP}{4}-${GUID_HEX_GROUP}{4}-${GUID_HEX_GROUP}{12}`;
const GUID_X_BYTE: string = `0x${GUID_HEX_GROUP}{1,2}`;
const GUID_X_FORM: string =
  `\\{\\s*0x${GUID_HEX_GROUP}{1,8}\\s*,` +
  `\\s*0x${GUID_HEX_GROUP}{1,4}\\s*,` +
  `\\s*0x${GUID_HEX_GROUP}{1,4}\\s*,` +
  `\\s*\\{` +
  `\\s*${GUID_X_BYTE}\\s*,`.repeat(7) +
  `\\s*${GUID_X_BYTE}\\s*` +
  `\\}\\s*\\}`;
const GUID_PATTERN: RegExp = new RegExp(
  `^(?:${GUID_HEX_GROUP}{32}|${GUID_D_FORM}|\\{${GUID_D_FORM}\\}|\\(${GUID_D_FORM}\\)|${GUID_X_FORM})$`
);

export interface ICellValidationIssue {
  clientId: string;
  column: string;
  message: string;
}

export const REQUIRED_VALUE_MESSAGE = "Required value is missing.";
export const INVALID_GUID_MESSAGE = "Enter a valid GUID.";
// Int32 / Int16 fit in a JS Number exactly, so we validate them as
// numbers. Int64 needs the full ±2^63 range and JS Number tops out at
// ±2^53; using Number(raw) here would silently round and reject
// legitimate database values past 2^53. So Int64 goes through BigInt
// instead — gives us the real range without precision loss.
const INT16_MIN: number = -32_768;
const INT16_MAX: number = 32_767;
const INT32_MIN: number = -2_147_483_648;
const INT32_MAX: number = 2_147_483_647;
// Signed 64-bit integer bounds. These match the backend's `long.TryParse`
// (Int64) so the client and server agree on what's valid.
const INT64_MIN_BIG: bigint = -9223372036854775808n;
const INT64_MAX_BIG: bigint = 9223372036854775807n;

const hasColumnDefault = (column: IDatabaseColumnModel): boolean => {
  return typeof column.defaultValue === "string" && column.defaultValue.trim().length > 0;
};

const isServerGeneratedColumn = (column: IDatabaseColumnModel): boolean => {
  return (
    column.isIdentity === true ||
    column.isAutoIncrement === true ||
    (column.isPk === true && column.kuikaDataType === "Guid") ||
    hasColumnDefault(column)
  );
};

export const isRequiredCellMissing = (column: IDatabaseColumnModel, value: unknown): boolean => {
  if (column.nullable !== false || isServerGeneratedColumn(column)) {
    return false;
  }
  if (value === null || value === undefined) {
    return true;
  }
  if (column.kuikaDataType === "Guid" && typeof value === "string" && value.trim().length === 0) {
    return true;
  }
  return false;
};

export const isGuidCellInvalid = (column: IDatabaseColumnModel, value: unknown): boolean => {
  if (column.kuikaDataType !== "Guid") {
    return false;
  }
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value !== "string") {
    return false;
  }
  const trimmed: string = value.trim();
  if (trimmed.length === 0) {
    return false;
  }
  return !GUID_PATTERN.test(trimmed);
};

// Returns true if `value` is "blank" for type-validation purposes —
// null / undefined / empty-string. Required-check ran earlier, so by
// the time we get here a blank value should be allowed and skipped.
const isBlankCellValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string" && value.trim().length === 0) {
    return true;
  }
  return false;
};

// Coerce the cell value to a string for type-format checks. The grid
// stores native types where it can (booleans, numbers, dates) but
// inputs hand back strings, so we accept both.
const cellValueAsString = (value: unknown): string => {
  return typeof value === "string" ? value : String(value);
};

const stringMaxLengthMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  if (column.kuikaDataType !== "String") {
    return null;
  }
  const max: number | null | undefined = column.maxLength;
  if (typeof max !== "number" || max <= 0) {
    return null;
  }
  if (typeof value !== "string") {
    return null;
  }
  if (value.length <= max) {
    return null;
  }
  return `Too long: max ${max.toLocaleString("en-US")} character${max === 1 ? "" : "s"} (got ${value.length.toLocaleString("en-US")}).`;
};

const intRangeMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  // Int64 takes the BigInt path so the full signed-64-bit range works.
  // Number(raw) would round on input past 2^53, rejecting values like
  // 9007199254740993 that the backend's `long.TryParse` accepts (and
  // that round-trip from existing rows via the string wire format).
  if (column.kuikaDataType === "Int64") {
    const raw: string = cellValueAsString(value).trim();
    if (raw.length === 0) {
      return null;
    }
    if (!/^-?\d+$/.test(raw)) {
      return "Enter a whole number (Int64).";
    }
    let parsed: bigint;
    try {
      parsed = BigInt(raw);
    } catch {
      // BigInt() can throw SyntaxError on certain inputs even after the
      // regex guard (it's strict about leading zeros in some implementations).
      return "Enter a whole number (Int64).";
    }
    if (parsed < INT64_MIN_BIG || parsed > INT64_MAX_BIG) {
      return `Value is out of range for Int64 (${INT64_MIN_BIG.toString()} … ${INT64_MAX_BIG.toString()}).`;
    }
    return null;
  }

  // Int16 / Int32 / AutoIncrement fit in a JS Number cleanly.
  let min: number;
  let max: number;
  let typeLabel: string;
  if (column.kuikaDataType === "Int16") {
    min = INT16_MIN;
    max = INT16_MAX;
    typeLabel = "Int16";
  } else if (column.kuikaDataType === "Int32" || column.kuikaDataType === "AutoIncrement") {
    min = INT32_MIN;
    max = INT32_MAX;
    typeLabel = "Int32";
  } else {
    return null;
  }
  const raw: string = cellValueAsString(value).trim();
  if (raw.length === 0) {
    return null;
  }
  if (!/^-?\d+$/.test(raw)) {
    return `Enter a whole number (${typeLabel}).`;
  }
  const parsed: number = Number(raw);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return `Value is out of range for ${typeLabel} (${min.toLocaleString("en-US")} … ${max.toLocaleString("en-US")}).`;
  }
  return null;
};

const decimalMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  if (column.kuikaDataType !== "Decimal" && column.kuikaDataType !== "Double") {
    return null;
  }
  const raw: string = cellValueAsString(value).trim();
  if (raw.length === 0) {
    return null;
  }
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) {
    // Reject scientific notation, comma decimals, junk. Backend's
    // invariant-culture decimal/double parse would reject the same set.
    return "Enter a valid number (use `.` as decimal separator, no thousands separator).";
  }
  if (column.kuikaDataType === "Decimal") {
    const scale: number | null | undefined = column.decimalScale;
    if (typeof scale === "number" && scale >= 0) {
      const dotIdx: number = raw.indexOf(".");
      const fractionLen: number = dotIdx === -1 ? 0 : raw.length - dotIdx - 1;
      if (fractionLen > scale) {
        return `Too many decimal places: max ${scale} (got ${fractionLen}).`;
      }
    }
  }
  return null;
};

// Location columns store `{"latitude": x, "longitude": y}` as a JSON
// string. The editor produces this shape directly, so this validator
// mostly catches paste-corrupted values and the corner where the user
// half-fills the pair (one coord typed, the other blank) — which would
// land as a half-formed object on the server and silently re-render
// blank on the next fetch.
const locationMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  if (column.kuikaDataType !== "Location") {
    return null;
  }
  if (value === null || value === undefined || value === "") {
    return null;
  }
  let lat: unknown;
  let lng: unknown;
  if (typeof value === "object") {
    const o: Record<string, unknown> = value as Record<string, unknown>;
    lat = o.latitude;
    lng = o.longitude;
  } else if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed === null || typeof parsed !== "object") {
        return 'Enter a valid location (expected `{"latitude": <number>, "longitude": <number>}`).';
      }
      const o: Record<string, unknown> = parsed as Record<string, unknown>;
      lat = o.latitude;
      lng = o.longitude;
    } catch {
      return 'Enter a valid location (expected `{"latitude": <number>, "longitude": <number>}`).';
    }
  } else {
    return "Enter a valid location.";
  }
  const latNum: number = typeof lat === "number" ? lat : Number.NaN;
  const lngNum: number = typeof lng === "number" ? lng : Number.NaN;
  const latPresent: boolean = lat !== null && lat !== undefined && Number.isFinite(latNum);
  const lngPresent: boolean = lng !== null && lng !== undefined && Number.isFinite(lngNum);
  if (!latPresent && !lngPresent) {
    return null;
  }
  if (!latPresent || !lngPresent) {
    return "Both latitude and longitude are required.";
  }
  if (latNum < -90 || latNum > 90) {
    return "Latitude must be between -90 and 90.";
  }
  if (lngNum < -180 || lngNum > 180) {
    return "Longitude must be between -180 and 180.";
  }
  return null;
};

// Sanity check on date / datetime / datetimeoffset cells. We don't try
// to mirror every format .NET accepts — too many cultural variants —
// but we catch the obvious "this isn't a date at all" case before the
// server does. ISO-8601-ish: YYYY-MM-DD optionally followed by Thh:mm
// and optional :ss[.ffff] and optional Z or ±HH:MM.
const DATE_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
const DATETIMEOFFSET_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;

const dateMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  let pattern: RegExp;
  let expected: string;
  if (column.kuikaDataType === "Date") {
    pattern = DATE_PATTERN;
    expected = "YYYY-MM-DD";
  } else if (column.kuikaDataType === "Datetime") {
    pattern = DATETIME_PATTERN;
    expected = "YYYY-MM-DD HH:mm[:ss]";
  } else if (column.kuikaDataType === "DatetimeOffset") {
    pattern = DATETIMEOFFSET_PATTERN;
    expected = "YYYY-MM-DD HH:mm[:ss] with Z or ±HH:MM offset";
  } else {
    return null;
  }
  const raw: string = cellValueAsString(value).trim();
  if (raw.length === 0) {
    return null;
  }
  if (!pattern.test(raw)) {
    return `Enter a valid ${column.kuikaDataType.toLowerCase()} (${expected}).`;
  }
  // Calendar-correctness check — the regex passes shapes like "2025-02-30"
  // and `Date.parse` happily rolls them over to March 2 instead of
  // rejecting, so we re-extract YMD and round-trip through the Date
  // constructor: if the components don't match, the input named a date
  // that doesn't exist. Skip for DatetimeOffset where parsing the
  // offset back out for round-trip is fiddly and the backend's own
  // DateTimeOffset.TryParse catches the same cases.
  const ymdMatch: RegExpExecArray | null = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymdMatch !== null && column.kuikaDataType !== "DatetimeOffset") {
    const y: number = Number(ymdMatch[1]);
    const m: number = Number(ymdMatch[2]);
    const d: number = Number(ymdMatch[3]);
    const trial: Date = new Date(y, m - 1, d);
    if (trial.getFullYear() !== y || trial.getMonth() !== m - 1 || trial.getDate() !== d) {
      return `Enter a valid ${column.kuikaDataType.toLowerCase()} (${expected}).`;
    }
  }
  return null;
};

export const cellValidationMessage = (column: IDatabaseColumnModel, value: unknown): string | null => {
  if (isRequiredCellMissing(column, value)) {
    return REQUIRED_VALUE_MESSAGE;
  }
  // Blank values that aren't required-missing are fine (nullable column,
  // server-generated default). Skip the type-format checks below.
  if (isBlankCellValue(value)) {
    return null;
  }
  if (isGuidCellInvalid(column, value)) {
    return INVALID_GUID_MESSAGE;
  }
  const stringMaxLen: string | null = stringMaxLengthMessage(column, value);
  if (stringMaxLen !== null) {
    return stringMaxLen;
  }
  const intRange: string | null = intRangeMessage(column, value);
  if (intRange !== null) {
    return intRange;
  }
  const decimal: string | null = decimalMessage(column, value);
  if (decimal !== null) {
    return decimal;
  }
  const date: string | null = dateMessage(column, value);
  if (date !== null) {
    return date;
  }
  const location: string | null = locationMessage(column, value);
  if (location !== null) {
    return location;
  }
  return null;
};

const columnsByName = (columns: IDatabaseColumnModel[]): Map<string, IDatabaseColumnModel> => {
  const result: Map<string, IDatabaseColumnModel> = new Map<string, IDatabaseColumnModel>();
  columns.forEach((column: IDatabaseColumnModel): void => {
    result.set(column.name, column);
  });
  return result;
};

// Refresh-time conflict detection. Pending updates capture `originals`
// at first-edit and lock the optimistic-concurrency check to that
// snapshot. A subsequent server-side change to the same row leaves
// pending state visually fine until the user clicks Save — and only
// THEN do they discover the conflict. This walks the freshly-loaded
// rows and flags any pending update whose captured originals diverge
// from the new server values, so the row's conflict surfaces before
// the user invests more typing.
//
// Returns a map of pk → conflict message for the editor to merge into
// saveIssuesByClientId. Rows not present in `rows` (paged off-screen,
// filtered out) are skipped — the divergence check is unreliable
// without a comparable row to read from.
export interface IConflictDetectionInputs {
  columns: IDatabaseColumnModel[];
  rows: Map<RowPkString, IColumnValueMap>;
  pending: IPendingChanges;
}

const isSameOriginalValue = (a: unknown, b: unknown): boolean => {
  // Null-equality first so the typical NULL → NULL case short-circuits
  // before primitive comparison; loose-equal to bridge `undefined` and
  // `null` since the backend coerces both to NULL at the SQL boundary.
  if (a === null || a === undefined) {
    return b === null || b === undefined;
  }
  if (b === null || b === undefined) {
    return false;
  }
  // Dates / numbers / strings compare by value via String coercion.
  // The reducer keeps originals as the raw row value (string from
  // server) so the comparand is also a string in nearly every case.
  return String(a) === String(b);
};

export const detectRefreshConflicts = (inputs: IConflictDetectionInputs): Map<string, string> => {
  const { rows, pending } = inputs;
  const conflicts: Map<string, string> = new Map<string, string>();
  pending.updates.forEach((update: IPendingRowUpdate, pk: RowPkString): void => {
    const currentRow: IColumnValueMap | undefined = rows.get(pk);
    if (currentRow === undefined) {
      return;
    }
    const divergedColumns: string[] = [];
    Object.keys(update.originals).forEach((columnName: string): void => {
      const captured: unknown = update.originals[columnName];
      const serverNow: unknown = currentRow[columnName];
      if (!isSameOriginalValue(captured, serverNow)) {
        divergedColumns.push(columnName);
      }
    });
    if (divergedColumns.length > 0) {
      const cols: string = divergedColumns.join(", ");
      conflicts.set(
        pk,
        `Row changed on the server since you started editing (column${
          divergedColumns.length === 1 ? "" : "s"
        }: ${cols}). Save will fail with a conflict — reload to see the current values.`
      );
    }
  });
  return conflicts;
};

export const buildLocalMutationValidationIssues = (
  columns: IDatabaseColumnModel[],
  pending: IPendingChanges
): ICellValidationIssue[] => {
  const byName: Map<string, IDatabaseColumnModel> = columnsByName(columns);
  const issues: ICellValidationIssue[] = [];
  pending.updates.forEach((update: IPendingRowUpdate, pk: RowPkString): void => {
    Object.keys(update.changes).forEach((columnName: string): void => {
      const column: IDatabaseColumnModel | undefined = byName.get(columnName);
      if (column === undefined) {
        return;
      }
      const message: string | null = cellValidationMessage(column, update.changes[columnName]);
      if (message !== null) {
        issues.push({ clientId: pk, column: columnName, message });
      }
    });
  });
  pending.inserts.filter(isPersistableInsert).forEach((insert: IPendingRowInsert): void => {
    columns.forEach((column: IDatabaseColumnModel): void => {
      const message: string | null = cellValidationMessage(column, insert.values[column.name]);
      if (message !== null) {
        issues.push({ clientId: insert.clientId, column: column.name, message });
      }
    });
  });
  return issues;
};
